import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomersService } from '../../../services/customers.service';
import { NewCustomerRequest, CustomerListItem } from '../../../interfaces/customer-service.interface';
import { InteractionsService } from '../../../services/interactions.service';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { 
  Individual, 
  Company, 
  CompanyOption, 
  NewCustomerDialogData, 
  NewCustomerDialogResult 
} from '../../../interfaces/new-customer-dialog.interface';
import { CustomerValidationService } from '../../../services/customer-validation.service';
import { DateService } from '../../../services/date.service';

@Component({
  selector: 'app-new-customer-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatCardModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatAutocompleteModule
  ],
  templateUrl: './new-customer-dialog.component.html',
  styleUrl: './new-customer-dialog.component.scss'
})
export class NewCustomerDialogComponent implements OnInit {
  isCompany = false;
  isSubmitting = false;
  isAnonymousInsured = false;
  
  // Ideiglenes biztosítottak tömbje (csak memóriában, nem kerülnek adatbázisba)
  static temporaryInsuredPersons: Individual[] = [];

  // Getter a típus váltó letiltásához
  get isTypeSwitchDisabled(): boolean {
    return this.isSubmitting || this.isEditMode || this.data.disableTypeSwitch || false;
  }

  // Getter a cég kiválasztás letiltásához
  get isCompanySelectionDisabled(): boolean {
    return this.isSubmitting || this.loadingCompanies || this.data.disableCompanySelection || false;
  }

  // Magánszemély adatok
  individualData: Individual = {
    fullName: '',
    address: '',
    birthDate: null,
    taxNumber: '',
    birthPlace: '',
    motherName: '',
    isExistingCustomer: true
  };

  // Születési dátum string reprezentáció (yyyy.mm.dd formátum)
  birthDateString: string = '';

  // Cég adatok
  companyData: Company = {
    name: '',
    taxNumber: '',
    registrationNumber: '',
    address: '',
    mainActivity: '',
    county: '',
    netRevenue: null,
    employeeCount: null,
    individuals: [],
    isExistingCustomer: true
  };

  counties = [
    'Budapest',
    'Bács-Kiskun',
    'Baranya',
    'Békés',
    'Borsod-Abaúj-Zemplén',
    'Csongrád-Csanád',
    'Fejér',
    'Győr-Moson-Sopron',
    'Hajdú-Bihar',
    'Heves',
    'Jász-Nagykun-Szolnok',
    'Komárom-Esztergom',
    'Nógrád',
    'Pest',
    'Somogy',
    'Szabolcs-Szatmár-Bereg',
    'Tolna',
    'Vas',
    'Veszprém',
    'Zala'
  ];

  // Új magánszemély hozzáadása a céghez
  newIndividual: Individual = this.createEmptyIndividual();
  newIndividualBirthDateString: string = '';
  showIndividualForm = false;

  // Cégek listája a dropdown-hoz
  availableCompanies: CompanyOption[] = [];
  selectedCompanyId: number | null = null;
  loadingCompanies = false;

  // Autocomplete funkcionalitás
  companySearchText = '';
  filteredCompanies: CompanyOption[] = [];
  selectedCompany: CompanyOption | null = null;

  constructor(
    public dialogRef: MatDialogRef<NewCustomerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NewCustomerDialogData,
    private customersService: CustomersService,
    private interactionsService: InteractionsService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private validationService: CustomerValidationService,
    private dateService: DateService
  ) {}

  get isEditMode(): boolean {
    return this.data.editMode === true;
  }

  get dialogTitle(): string {
    return this.isEditMode ? 'Ügyfél szerkesztése' : 'Új ügyfél hozzáadása';
  }

  get dialogIcon(): string {
    return this.isEditMode ? 'edit' : 'person_add';
  }

  get saveButtonText(): string {
    return this.isEditMode ? 'Frissítés' : 'Mentés';
  }

  ngOnInit(): void {
    this.initializeComponentMode();
    this.initializePreselectedData();
    this.initializeEditMode();
    this.loadAvailableCompanies();
  }

  private initializeComponentMode(): void {
    // Ha kényszerített individual mód van beállítva
    if (this.data.forceIndividualMode) {
      this.isCompany = false;
    }
  }

  private initializePreselectedData(): void {
    // Ha van előre kiválasztott cég ID
    if (this.data.selectedCompanyId) {
      this.selectedCompanyId = this.data.selectedCompanyId;
    }

    // Ha van előre kiválasztott cég objektum
    if (this.data.preselectedCompany) {
      this.selectedCompany = this.data.preselectedCompany;
      this.selectedCompanyId = this.data.preselectedCompany.id;
    }
  }

  private initializeEditMode(): void {
    // Ha edit módban vagyunk, töltsd fel az adatokat először
    if (this.data.editMode && (this.data.customerData || this.data.individualData || this.data.companyData)) {
      this.initializeForEdit();
    }
  }

  private initializeForEdit(): void {
    if (this.data.customerType === 'individual' && this.data.individualData) {
      this.initializeIndividualEdit();
    } else {
      this.initializeCompanyEdit();
    }
  }

  private initializeIndividualEdit(): void {
    this.isCompany = false;
    const individual = this.data.individualData!;
    
    this.individualData = {
      fullName: individual.fullName || '',
      address: individual.address || '',
      birthDate: individual.birthDate ? new Date(individual.birthDate) : null,
      taxNumber: individual.taxNumber || '',
      birthPlace: individual.birthPlace || '',
      motherName: individual.motherName || '',
      isExistingCustomer: individual.isExistingCustomer !== undefined ? individual.isExistingCustomer : true,
      companyId: individual.companyId
    };

    this.birthDateString = this.dateService.formatDateToString(this.individualData.birthDate);
  }

  private initializeCompanyEdit(): void {
    this.isCompany = true;
    
    if (this.data.companyData) {
      this.initializeFromCompanyData();
    } else if (this.data.customerData) {
      this.initializeFromCustomerData();
    }
  }

  private initializeFromCompanyData(): void {
    const company = this.data.companyData!;
    this.companyData = {
      name: company.companyname || '',
      taxNumber: company.taxNumber || '',
      registrationNumber: company.registrationNumber || '',
      address: company.address || '',
      mainActivity: company.mainActivity || '',
      county: company.county || '',
      netRevenue: company.netRevenue || null,
      employeeCount: company.employeeCount || null,
      individuals: [],
      isExistingCustomer: company.isExistingCustomer !== undefined ? company.isExistingCustomer : true
    };
  }

  private initializeFromCustomerData(): void {
    const customer = this.data.customerData!;
    this.companyData = {
      name: customer.companyName || '',
      taxNumber: customer.taxNumber || '',
      registrationNumber: customer.registrationNumber || '',
      address: customer.address || '',
      mainActivity: customer.mainActivity || '',
      county: customer.county || '',
      netRevenue: null,
      employeeCount: null,
      individuals: [],
      isExistingCustomer: customer.isExistingCustomer !== undefined ? customer.isExistingCustomer : true
    };
  }

  private createEmptyIndividual(): Individual {
    return {
      fullName: '',
      address: '',
      birthDate: null,
      taxNumber: '',
      birthPlace: '',
      motherName: '',
      isExistingCustomer: true,
      companyId: undefined
    };
  }

  onTypeChange(): void {
    // Reset data when switching types
    this.individualData = this.createEmptyIndividual();
    this.companyData = {
      name: '',
      taxNumber: '',
      registrationNumber: '',
      address: '',
      mainActivity: '',
      county: '',
      netRevenue: null,
      employeeCount: null,
      individuals: [],
      isExistingCustomer: true
    };
    this.newIndividual = this.createEmptyIndividual();
    this.showIndividualForm = false;

    // Reset company selection when switching types
    this.selectedCompanyId = null;
  }  private loadAvailableCompanies(): void {
    this.loadingCompanies = true;
    this.customersService.getCompanies().subscribe({
      next: (companies) => {
        this.availableCompanies = companies.map(company => ({
          id: company.companyid || company.id,
          name: company.companyname || company.name,
          registrationNumber: company.companyregistrationnumber || company.registrationNumber || '',
          displayText: `ID: ${company.companyid || company.id} | ${company.companyname || company.name} ${company.companyregistrationnumber || company.registrationNumber ? '(' + (company.companyregistrationnumber || company.registrationNumber) + ')' : ''}`
        }));
        this.filteredCompanies = [...this.availableCompanies]; // Inicializáljuk a szűrt listát
        this.loadingCompanies = false;
        this.cdr.detectChanges();
        
        // Ha edit módban vagyunk és van companyId, állítsuk be a kiválasztott céget
        this.setSelectedCompanyAfterLoad();
      },
      error: (error) => {
        this.loadingCompanies = false;
        this.cdr.detectChanges();
        this.snackBar.open(
          'Hiba történt a cégek betöltése során',
          'Bezárás',
          {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  private setSelectedCompanyAfterLoad(): void {
    // Ha edit módban vagyunk és individual szerkesztés van, és van companyId
    if (this.isEditMode && !this.isCompany && this.individualData?.companyId) {
      const companyId = this.individualData.companyId;
      const company = this.availableCompanies.find(c => c.id === companyId);
      if (company) {
        this.selectedCompany = company;
        this.selectedCompanyId = companyId;
        this.companySearchText = company.displayText;
        
        // Kényszerítsük a change detection-t többször is
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 100);
        
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 500);
        
      } else {
        console.warn('⚠️ Nem található a cég az ID alapján:', companyId);
        console.warn('⚠️ Elérhető cégek:', this.availableCompanies.map(c => ({id: c.id, name: c.name})));
      }
    } else {
      console.log('ℹ️ Nem teljesülnek a feltételek a cég beállításához');
    }
  }

  // Autocomplete metódusok
  onCompanyInputChange(value: any): void {
    if (typeof value === 'string') {
      // Ha string-et gépelnek be, akkor szűrjük a cégeket
      this.companySearchText = value;
      this.filterCompanies(value);
      // Töröljük a kiválasztott céget, mert most gépelnek
      if (this.selectedCompany && this.selectedCompany.displayText !== value) {
        this.selectedCompany = null;
        this.selectedCompanyId = null;
        this.individualData.companyId = undefined;
      }
    } else if (value && typeof value === 'object') {
      // Ha objektumot választanak ki, beállítjuk
      this.selectedCompany = value;
      this.selectedCompanyId = value.id;
      this.individualData.companyId = value.id;
      this.companySearchText = value.displayText;
    } else {
      // Ha null-t választanak
      this.selectedCompany = null;
      this.selectedCompanyId = null;
      this.individualData.companyId = undefined;
      this.companySearchText = '';
    }
  }

  onCompanySearchChange(searchText: string): void {
    this.companySearchText = searchText;
    this.filterCompanies(searchText);
  }

  private filterCompanies(searchText: string): void {
    if (!searchText || searchText.trim() === '') {
      this.filteredCompanies = [...this.availableCompanies];
      return;
    }

    const lowercaseSearch = searchText.toLowerCase();
    this.filteredCompanies = this.availableCompanies.filter(company =>
      company.name.toLowerCase().includes(lowercaseSearch) ||
      company.registrationNumber.toLowerCase().includes(lowercaseSearch) ||
      company.displayText.toLowerCase().includes(lowercaseSearch)
    );
  }

  onCompanySelected(event: any): void {
    const selectedCompany = event.option.value;
    if (selectedCompany && selectedCompany.id) {
      this.selectedCompany = selectedCompany;
      this.selectedCompanyId = selectedCompany.id;
      this.individualData.companyId = selectedCompany.id;
    } else {
      // "Nincs cég kiválasztva" opció
      this.selectedCompany = null;
      this.selectedCompanyId = null;
      this.individualData.companyId = undefined;
      this.companySearchText = '';
    }
  }

  displayCompanyName = (company: any | null): string => {
    if (!company) {
      return '';
    }
    // Többféle formátum támogatása
    const displayName = company.displayText || company.display || company.companyname || company.name || '';
    return displayName;
  };

  showAddIndividualForm(): void {
    this.showIndividualForm = true;
    this.newIndividual = this.createEmptyIndividual();
    this.newIndividualBirthDateString = '';
  }

  addIndividualToCompany(): void {
    if (this.validationService.isValidIndividual(this.newIndividual, true, this.newIndividualBirthDateString)) {
      this.companyData.individuals.push({ ...this.newIndividual });
      
      // Form elrejtése és reset
      this.showIndividualForm = false;
      this.newIndividual = this.createEmptyIndividual();
      this.newIndividualBirthDateString = '';
      
      this.snackBar.open(
        `Személy "${this.companyData.individuals[this.companyData.individuals.length - 1].fullName}" hozzáadva a céghez`,
        'Bezárás',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        }
      );
    } else {
      this.snackBar.open(
        'Kérem töltse ki legalább a teljes nevet!',
        'Bezárás',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        }
      );
    }
  }

  cancelAddIndividual(): void {
    this.showIndividualForm = false;
    this.newIndividual = this.createEmptyIndividual();
    this.newIndividualBirthDateString = '';
  }

  removeIndividualFromCompany(index: number): void {
    this.companyData.individuals.splice(index, 1);
  }

  // Helper method for template validation
  isNewIndividualValid(): boolean {
    return this.validationService.isValidIndividual(this.newIndividual, true, this.newIndividualBirthDateString);
  }

  // Helper methods for template date validation
  isValidBirthDateFormat(dateString: string): boolean {
    return this.validationService.isValidBirthDateFormat(dateString);
  }

  onSave(): void {
    this.isSubmitting = true;

    // Név nélküli biztosított esetén csak helyi tömbbe mentés
    if (this.isAnonymousInsured) {
      this.saveAnonymousInsured();
      return;
    }

    // Edit módban - individual szerkesztés
    if (this.isEditMode && !this.isCompany) {
      this.handleIndividualEdit();
      return;
    }

    // Edit módban - cég szerkesztés
    if (this.isEditMode && this.isCompany) {
      this.handleCompanyEdit();
      return;
    }

    // Új ügyfél létrehozása
    this.handleCreateNewCustomer();
  }

  private handleIndividualEdit(): void {
    const changes = this.detectIndividualChanges();
    
    if (Object.keys(changes).length === 0) {
      this.isSubmitting = false;
      this.showInfoMessage('Nincs változás az adatokban');
      return;
    }
    
    this.showInfoMessage(`${Object.keys(changes).length} mező módosítva, mentés folyamatban...`);
    
    const customerId = this.data.individualData?.id;
    if (!customerId) {
      this.isSubmitting = false;
      this.showErrorMessage('Hiba: Nem található az ügyfél azonosító');
      return;
    }

    const updatedIndividualData = this.prepareIndividualDataForSave();
    
    this.customersService.updateCustomer(customerId, updatedIndividualData).subscribe({
      next: (response) => {
        if (Object.keys(changes).length > 0) {
          this.createIndividualEditInteraction(customerId, changes);
        }
        
        this.showSuccessMessage(`✅ ${this.individualData.fullName} adatai sikeresen frissítve!`);
        this.closeDialogWithResult('individual', updatedIndividualData, undefined, response);
      },
      error: (error) => this.handleSaveError(error)
    });
  }

  private handleCompanyEdit(): void {
    const changes = this.detectCompanyChanges();
    
    const companyId = this.data.companyData?.id || this.data.customerData?.id;
    if (!companyId) {
      this.isSubmitting = false;
      this.showErrorMessage('Hiba: Nem található a cég azonosító');
      return;
    }

    this.customersService.updateCompany(companyId, this.companyData).subscribe({
      next: (response) => {
        if (Object.keys(changes).length > 0) {
          this.createCompanyEditInteraction(companyId, changes);
        }
        
        this.showSuccessMessage(`✅ ${this.companyData.name} adatai sikeresen frissítve!`);
        this.closeDialogWithResult('company', undefined, this.companyData, response);
      },
      error: (error) => this.handleSaveError(error)
    });
  }

  private handleCreateNewCustomer(): void {
    const individualWithCompany = this.prepareIndividualDataForSave();
    (individualWithCompany as any).isAnonymousInsured = this.isAnonymousInsured;

    const request: NewCustomerRequest = {
      type: this.isCompany ? 'company' : 'individual',
      individual: this.isCompany ? undefined : individualWithCompany,
      company: this.isCompany ? this.companyData : undefined
    };

    this.customersService.createCustomer(request).subscribe({
      next: (response) => {
        const customerType = this.isCompany ? 'cég' : 'magánszemély';
        const customerName = this.isCompany ? this.companyData.name : this.individualData.fullName;
        
        this.showSuccessMessage(`✅ ${customerType} (${customerName}) sikeresen létrehozva!`);
        this.closeDialogWithResult(
          this.isCompany ? 'company' : 'individual',
          this.isCompany ? undefined : this.individualData,
          this.isCompany ? this.companyData : undefined,
          response
        );
      },
      error: (error) => this.handleSaveError(error, 8000)
    });
  }

  private detectIndividualChanges(): any {
    const changes: any = {};
    if (!this.data.individualData) return changes;
    
    const original = this.data.individualData;
    const modified = this.individualData;
    
    if (original.fullName !== modified.fullName) {
      changes.fullName = { before: original.fullName, after: modified.fullName };
    }
    if (original.address !== modified.address) {
      changes.address = { before: original.address, after: modified.address };
    }
    if (original.taxNumber !== modified.taxNumber) {
      changes.taxNumber = { before: original.taxNumber, after: modified.taxNumber };
    }
    if (original.birthPlace !== modified.birthPlace) {
      changes.birthPlace = { before: original.birthPlace, after: modified.birthPlace };
    }
    if (original.motherName !== modified.motherName) {
      changes.motherName = { before: original.motherName, after: modified.motherName };
    }
    
    // Születési dátum összehasonlítás
    const originalBirthDate = original.birthDate ? new Date(original.birthDate).toISOString().split('T')[0] : null;
    const modifiedBirthDate = modified.birthDate ? new Date(modified.birthDate).toISOString().split('T')[0] : null;
    if (originalBirthDate !== modifiedBirthDate) {
      changes.birthDate = { before: originalBirthDate, after: modifiedBirthDate };
    }
    
    return changes;
  }

  private detectCompanyChanges(): any {
    const changes: any = {};
    if (!this.data.companyData) return changes;
    
    const original = this.data.companyData;
    const modified = this.companyData;
    
    if (original.companyname !== modified.name) {
      changes.companyname = { before: original.companyname, after: modified.name };
    }
    if (original.taxNumber !== modified.taxNumber) {
      changes.taxNumber = { before: original.taxNumber, after: modified.taxNumber };
    }
    if (original.registrationNumber !== modified.registrationNumber) {
      changes.registrationNumber = { before: original.registrationNumber, after: modified.registrationNumber };
    }
    if (original.address !== modified.address) {
      changes.address = { before: original.address, after: modified.address };
    }
    if (original.mainActivity !== modified.mainActivity) {
      changes.mainActivity = { before: original.mainActivity, after: modified.mainActivity };
    }
    if (original.county !== modified.county) {
      changes.county = { before: original.county, after: modified.county };
    }
    if (original.netRevenue !== modified.netRevenue) {
      changes.netRevenue = { before: original.netRevenue, after: modified.netRevenue };
    }
    if (original.employeeCount !== modified.employeeCount) {
      changes.employeeCount = { before: original.employeeCount, after: modified.employeeCount };
    }
    
    return changes;
  }

  private prepareIndividualDataForSave(): Individual {
    const individualWithCompany = { ...this.individualData };
    
    // Ha van kiválasztott cég, hozzáadjuk az ID-ját
    if (!this.isCompany && this.selectedCompanyId) {
      individualWithCompany.companyId = this.selectedCompanyId;
    }
    
    // Biztosítsuk, hogy a születési dátum megfelelően van beállítva
    if (!this.isCompany) {
      const parsedDate = this.dateService.parseStringToDate(this.birthDateString);
      individualWithCompany.birthDate = parsedDate;
    }
    
    return individualWithCompany;
  }

  private closeDialogWithResult(type: 'individual' | 'company', individual?: Individual, company?: Company, backendResponse?: any): void {
    const result: NewCustomerDialogResult = {
      action: 'save',
      data: {
        type,
        individual,
        company,
        backendResponse
      }
    };
    this.dialogRef.close(result);
  }

  private handleSaveError(error: any, duration: number = 5000): void {
    setTimeout(() => {
      this.isSubmitting = false;
      this.cdr.detectChanges();
    });
    
    this.showErrorMessage(`❌ Hiba történt: ${error.error?.detail || error.message || 'Ismeretlen hiba'}`, duration);
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
  }

  private showErrorMessage(message: string, duration: number = 5000): void {
    this.snackBar.open(message, 'Bezárás', {
      duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  private showInfoMessage(message: string): void {
    this.snackBar.open(message, 'Bezárás', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['info-snackbar']
    });
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  get canSave(): boolean {
    if (this.isCompany) {
      return this.validationService.isValidCompany(this.companyData);
    } else {
      return this.validationService.isValidIndividual(this.individualData, false, this.birthDateString);
    }
  }

  // Születési dátum string változásának kezelése
  onBirthDateStringChange(): void {
    const parsedDate = this.dateService.parseStringToDate(this.birthDateString);
    this.individualData.birthDate = parsedDate;
  }

  // Új individual születési dátum string változásának kezelése
  onNewIndividualBirthDateStringChange(): void {
    const parsedDate = this.dateService.parseStringToDate(this.newIndividualBirthDateString);
    this.newIndividual.birthDate = parsedDate;
  }

  // Név nélküli biztosított checkbox változásának kezelése
  onAnonymousInsuredChange(): void {
    if (this.isAnonymousInsured) {
      // Automatikus név generálás
      const nextNumber = NewCustomerDialogComponent.temporaryInsuredPersons.length + 1;
      this.individualData.fullName = `Biztosított ${nextNumber}`;
    } else {
      // Ha kikapcsoljuk, üres név
      this.individualData.fullName = '';
    }
  }

  // Név nélküli biztosított mentése helyi tömbbe (nem adatbázisba)
  saveAnonymousInsured(): void {
    // Validáció
    if (!this.validationService.isValidIndividual(this.individualData, false, this.birthDateString)) {
      this.isSubmitting = false;
      return;
    }

    // Új ideiglenes ügyfél létrehozása
    const temporaryCustomer: Individual = {
      fullName: this.individualData.fullName,
      address: '',
      birthDate: this.individualData.birthDate,
      taxNumber: '',
      birthPlace: '',
      motherName: '',
      isExistingCustomer: false
    };

    // Hozzáadás a helyi tömbhöz
    NewCustomerDialogComponent.temporaryInsuredPersons.push(temporaryCustomer);
   
    // Dialog bezárása az új ügyféllel
    this.isSubmitting = false;
    this.dialogRef.close({
      success: true,
      customer: temporaryCustomer,
      isTemporary: true
    });
  }

  /**
   * Céginformációk szerkesztéséhez interakció létrehozása
   */
  private createCompanyEditInteraction(companyId: number, changes: any): void {
    const statusText = 'Céginformációk módosítva';
    const affectedEntitiesData = {
      type: 'company_edit',
      changes: changes
    };
    
    const interactionData: any = {
      companyid: companyId,
      status: statusText,
      affected_entities: JSON.stringify([affectedEntitiesData])
    };

    this.interactionsService.createInteraction(interactionData).subscribe();
  }

  /**
   * Magánszemély szerkesztéséhez interakció létrehozása
   */
  private createIndividualEditInteraction(customerId: number, changes: any): void {
    const statusText = 'Ügyfél adatai módosítva';
    const affectedEntitiesData = {
      type: 'individual_edit',
      changes: changes
    };
    
    const interactionData: any = {
      customerid: customerId,
      status: statusText,
      affected_entities: JSON.stringify([affectedEntitiesData])
    };

    this.interactionsService.createInteraction(interactionData).subscribe();
  }
}